// 進行中存款訂單的本地化持久化（client only）
//
// 為何需要：
// store 中的 activeDepositOrder 用於浮動按鈕顯示，但 reload / 切頁 / SSR hydration 後
// Pinia state 會被重置。透過 storage util（localStorage）持久化，TTL 設為到 expiresAt 的
// 剩餘秒數，過期自動 removeItem，避免顯示已失效的訂單。
//
// 設計重點：
// - 用 store.$subscribe 觀察 activeDepositOrder 變化（涵蓋 setActiveDepositOrder /
//   clearActiveDepositOrder 兩條路徑）
// - applyId+expiresAt 簽章 dedupe，避免 watcher 重複觸發時連續寫 localStorage
// - hydrate 時不驗 uidSite（userInfo 還沒 fetch）；改在 userInfoStore.$subscribe 內驗
//   uid 不符時立即清除
// - 監聽 setUserInfo：登出（uid 變空）或換帳號（uid 不一致）皆觸發 clear

import { useDepositInProgressStore } from '@/stores/payment/depositInProgress'
import { useUserInfoStore } from '@/stores/user/userInfo'
import { storage } from '@/utils/storage'

const STORAGE_KEY = 'activeDepositOrder'

function isValidOrder(order) {
  return !!order && !!order.applyId && order.expiresAt > Date.now()
}

export default defineNuxtPlugin(() => {
  const store = useDepositInProgressStore()
  const userInfoStore = useUserInfoStore()

  // 1. Hydrate
  try {
    const stored = storage.get(STORAGE_KEY)
    if (isValidOrder(stored)) {
      store.setActiveDepositOrder(stored)
    } else if (stored) {
      storage.remove(STORAGE_KEY)
    }
  } catch (_) {
    // ignore parse errors; storage util already logged
  }

  // 2. Persist on store changes（dedupe by applyId|expiresAt）
  let lastSig = ''
  store.$subscribe((mutation, state) => {
    const order = state.activeDepositOrder
    const sig = order ? `${order.applyId}|${order.expiresAt}` : ''
    if (sig === lastSig) return
    lastSig = sig
    if (!order) {
      storage.remove(STORAGE_KEY)
      return
    }
    if (!isValidOrder(order)) {
      storage.remove(STORAGE_KEY)
      return
    }
    const ttlSec = Math.max(1, Math.ceil((order.expiresAt - Date.now()) / 1000))
    storage.set(STORAGE_KEY, order, { maxAge: ttlSec })
  })

  // 3. uid 不符 / 登出 → 清除
  // userInfoStore.$subscribe 會被任何 userInfo 欄位更新（balance、bank_amount 等高頻變動）觸發；
  // 用 lastUid dedupe 避免每次都跑比對邏輯。
  //
  // 重要：必須區分「userInfo 尚未 fetch（uid 為空，prevUid 為 null）」與「真正登出（prevUid 是
  // 有值的 uid，currentUid 變空）」。否則 hydrate 後第一次 userInfoStore mutation（如
  // setStatus('fetching')）就會把剛從 localStorage 還原的訂單當成登出清掉，造成重整後浮動按鈕消失。
  let lastUid = null
  userInfoStore.$subscribe((mutation, state) => {
    const currentUid = state.userInfo?.uid_site || ''
    if (currentUid === lastUid) return
    const prevUid = lastUid
    lastUid = currentUid

    const order = store.activeDepositOrder
    if (!order) return
    // 真正登出：之前有 uid 變空（prevUid 為 null 代表尚未觀察，不算登出）
    if (prevUid && !currentUid) {
      store.clearActiveDepositOrder()
      return
    }
    // 換帳號（uid 不一致）：僅在 currentUid 真有值時判斷
    if (currentUid && order.uidSite && String(order.uidSite) !== String(currentUid)) {
      store.clearActiveDepositOrder()
    }
  })

  // 4. 跨分頁同步：localStorage 在「其他」分頁觸發 storage 事件（同分頁自己的寫入不會觸發）。
  //
  //    兩個方向都要處理：
  //    a) newValue === null（remove）：另一分頁清掉訂單（socket / 用戶 X / 30s polling），
  //       本分頁同步清空 state，避免繼續顯示已結算的浮動按鈕。
  //    b) newValue 有值（set）：另一分頁提交了新訂單。本分頁若是後開的會在 hydrate 階段
  //       讀到，但若是先開的話只能仰賴這條路徑同步；不處理會讓 Tab A 提交後 Tab B 永遠
  //       看不到浮動按鈕直到手動 reload。
  //
  //    雙向同步無限迴圈防呆：store.$subscribe 寫 localStorage 時的 sig dedupe（applyId|expiresAt）
  //    +「同分頁寫入不觸發 storage 事件」這條瀏覽器規則 → 自身回放的事件天然不會發生。
  //    多分頁互相 set 同一個 (applyId, expiresAt) 時，後續 dedupe 也會 short-circuit。
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return

      // a) remove → clear
      if (event.newValue === null) {
        if (store.activeDepositOrder) store.clearActiveDepositOrder()
        return
      }

      // b) set → hydrate（並對 sig dedupe 避免重複觸發 $subscribe 寫回）
      try {
        const wrapper = JSON.parse(event.newValue)
        const incoming = wrapper && typeof wrapper === 'object' && 'value' in wrapper ? wrapper.value : wrapper
        if (!isValidOrder(incoming)) return
        const current = store.activeDepositOrder
        if (current && String(current.applyId) === String(incoming.applyId) && current.expiresAt === incoming.expiresAt) return
        store.setActiveDepositOrder(incoming)
      } catch (_) {
        // 忽略 parse 錯誤（其他分頁寫入的非 JSON、或不完整的資料）
      }
    })
  }
})
